import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { clearNotification } from '../../reduser/notificationSlice';
import axiosInstance from '../../utils/axiosInstanse';
import host from "../../host";
import unpaid_date from '../../utils/checkType';
import './style.css';
import { showNotification } from '../../utils/notificationUtils';

function CardNote({ note: initialNote }) {
  const [note, setNote] = useState(initialNote);
  const [sending, setSending] = useState(false);
  const username = useSelector(state => state.user.username);
  const notification = useSelector(state => state.notification);
  const dispatch = useDispatch();

  useEffect(() => {
    if (notification.show) {
      const now = Date.now();
      const elapsedTime = now - notification.startTime;
      const remainingTime = 4000 - elapsedTime;

      if (remainingTime > 0) {
        const timer = setTimeout(() => {
          dispatch(clearNotification());
        }, remainingTime);
        return () => clearTimeout(timer);
      } else {
        dispatch(clearNotification());
      }
    }
  }, [notification, dispatch]);

  const updateNoteStatus = (solution) => {
    if (solution === false && username === note.last_supervisor) {
      return { is_denied: true };
    } else if (solution === true && username === note.last_supervisor) {
      return { is_signed: true };
    }
    return {};
  };

  const handleSubmit = async (id, solution) => {
    if (sending) return;
    try {
      setSending(true);
      const response = await axiosInstance.post(`${host}api/sign_notes/`, {
        tab_num: note.tab_num,
        solution: solution,
        id: id
      });
      if (response.status === 200) {
        setSending(false);
        setNote(prevNote => {
          const statusUpdate = updateNoteStatus(solution);
          return {
            ...prevNote,
            ...statusUpdate,
            first_supervisor_answer: username === note.first_supervisor ? solution : prevNote.first_supervisor_answer,
            last_supervisor_answer: username === note.last_supervisor ? solution : prevNote.last_supervisor_answer
          };
        });
      }
    } catch (error) {
      showNotification(dispatch, 'Ошибка ответа', 'error');
      setSending(false);
    }
  };

  const showButtons = (
    (username === note.first_supervisor && note.first_supervisor_answer === null && note.is_new) ||
    (username === note.last_supervisor && note.last_supervisor_answer === null && note.is_new)
  );

  const getCardMod = () => {
    if (note.is_denied || note.sap_approved === false) {
      return 'sn-card--rejected';
    } else if (note.sap_approved) {
      return 'sn-card--approved';
    } else if (note.is_signed && note.sap_approved === null) {
      return 'sn-card--pending';
    }
    return 'sn-card--default';
  };

  const cardMod = getCardMod();

  return (
    <motion.div
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.12 }}
      style={{ width: '100%' }}
    >
      <div className={`sn-card ${cardMod}`}>
        {/* Header */}
        <div className="sn-card-header">
          <span className="sn-card-tab">Табельный {note.tab_num}</span>
          <span className="sn-card-id">
            Заявка № {note.id}
            {note.created_at && ` от ${new Date(note.created_at).toLocaleString('ru-RU', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}`}
          </span>
        </div>

        {/* Body */}
        <div className="sn-card-body">
          {note.delete_approved && (
            <div className="sn-delete-mark">
              Отменена пользователем
            </div>
          )}

          <div className="sn-worker">Сотрудник: {note.worker}</div>

          <div className="sn-date">Разрешение на выход с завода: {note.date}</div>

          <div className="sn-time-row">
            <span className="sn-time-item">С {note.time_from}</span>
            <span className="sn-time-item">
              {note.time_until ? `По ${note.time_until}` : 'Без времени'}
            </span>
          </div>

          <div className="sn-reason">
            {note.official ? 'По служебному'
              : note.official_in_plane ? 'Служебная внутри завода'
                : note.unpaid_vacation_time ? unpaid_date(note)
                  : note.personal ? 'По личному'
                    : 'Не определено'}
          </div>

          {note.description && (
            <div className="sn-desc"><strong>Описание причины:</strong> {note.description}</div>
          )}

          <div className="sn-reason">
            {note.plot && `Участок: ${note.plot}`}
          </div>

          {/* Solution block */}
          <div className="sn-solution">
            <div className="sn-solution-col">
              <div className="sn-solution-heading">Руководитель</div>
              <div className="sn-solution-item">
                {note.first_supervisor} —&nbsp;
                {note.first_supervisor_answer === true ? (
                  <span className="sn-solution-item--success">Одобрено</span>
                ) : note.first_supervisor_answer === false ? (
                  <span className="sn-solution-item--danger">Отклонено</span>
                ) : note.last_supervisor_answer === true ? (
                  <span className="sn-solution-item--success">Одобрено вышестоящим</span>
                ) : note.last_supervisor_answer === false ? (
                  <span className="sn-solution-item--danger">Отклонено вышестоящим</span>
                ) : (
                  <span className="sn-solution-item--pending">В процессе</span>
                )}
              </div>
            </div>
            <div className="sn-solution-col">
              <div className="sn-solution-heading">Руководитель</div>
              <div className="sn-solution-item">
                {note.last_supervisor} —&nbsp;
                {note.last_supervisor_answer === true ? (
                  <span className="sn-solution-item--success">Одобрено</span>
                ) : note.last_supervisor_answer === false ? (
                  <span className="sn-solution-item--danger">Отклонено</span>
                ) : (
                  <span className="sn-solution-item--pending">В процессе</span>
                )}
              </div>
            </div>
          </div>

          {note.nvo_text && (
            <div className="sn-nvo">
              {note.nvo_text}
            </div>
          )}

          {note.sap_approved === false && (
            <div className="sn-sap-error">
              Отклонено системой — {note.comment_from_sap}
            </div>
          )}
        </div>

        {/* Action buttons */}
        {showButtons && (
          <div className="sn-actions">
            <motion.button
              className="sn-btn sn-btn--approve"
              onClick={() => handleSubmit(note.id, true)}
              disabled={sending}
              whileTap={{ scale: 0.97 }}
            >
              {sending
                ? <span className="sn-spinner" />
                : '✓ Одобрить'}
            </motion.button>
            <motion.button
              className="sn-btn sn-btn--reject"
              onClick={() => handleSubmit(note.id, false)}
              disabled={sending}
              whileTap={{ scale: 0.97 }}
            >
              {sending
                ? <span className="sn-spinner" />
                : '✕ Отклонить'}
            </motion.button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default CardNote;